import {useAuth} from "@/auth";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";


const uniquePCOptions=(values)=>[...new Set(values.map(v=>String(v??"").trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b,undefined,{numeric:true}));
function PCMasterPicker({label,value,onChange,options,allowMultiline=false,tone=""}) {
  const [open,setOpen]=useState(false);
  const [typed,setTyped]=useState(false);
  const root=useRef(null);
  useEffect(()=>{
    if(!open) return;
    const close=e=>{if(root.current&&!root.current.contains(e.target)){setOpen(false);setTyped(false);}};
    document.addEventListener("pointerdown",close,true);
    return ()=>document.removeEventListener("pointerdown",close,true);
  },[open]);
  const current=String(value??"");
  const terms=current.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches=(typed?options.filter(opt=>terms.every(term=>String(opt).toLowerCase().includes(term))):options).slice(0,100);
  const focus=e=>{setOpen(true);setTyped(false);requestAnimationFrame(()=>e.target.select?.());};
  const change=e=>{setTyped(true);onChange(e.target.value);setOpen(true);};
  const choose=opt=>{onChange(opt);setTyped(false);setOpen(false);};
  return <div className="relative mt-1.5 min-w-0" ref={root} data-testid="pc-integrated-master-picker">
    <div className="relative">
      {allowMultiline?
        <Textarea value={current} onChange={change} onFocus={focus}
          className={"min-h-20 w-full min-w-0 bg-white pr-7 text-sm "+tone} placeholder={"Enter or choose "+label.toLowerCase()}
          role="combobox" aria-expanded={open} aria-autocomplete="list"/>:
        <Input value={current} onChange={change} onFocus={focus}
          className={"h-11 w-full min-w-0 bg-white pr-7 text-sm "+tone} placeholder={"Enter or choose "+label.toLowerCase()}
          role="combobox" aria-expanded={open} aria-autocomplete="list"/>}
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">⌄</span>
    </div>
    {open&&<div className="absolute z-50 top-full mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-blue-200 bg-white shadow-xl">
      {matches.length?matches.map((opt,i)=><button type="button" key={String(opt)+i}
        onClick={()=>choose(opt)}
        className="block w-full whitespace-normal break-words border-b border-slate-100 px-3 py-2 text-left text-sm leading-5 text-slate-700 hover:bg-blue-50 focus:bg-blue-50">
        {opt}
      </button>):<div className="px-3 py-3 text-xs text-slate-500">
        {options.length?"No matching master entry. Keep typing to use a custom value.":"No master entries available. You can type a custom value."}
      </div>}
    </div>}
  </div>;
}
// pc-integrated-value-master-picker-20260930

const fields = [
  ["customer_name","Customer Name"],["contact","Contact Person with Mobile No."],
  ["container_type","Type of Container"],["vehicle_model","Vehicle Model"],["external_size","External Size - mm"],["internal_size","Internal Size - mm"],
  ["inside_wall","Inside Finish - Vertical Walls"],["inside_roof","Inside Finish - Roof"],["outside_finish","Outside Finish"],
  ["base","Base"],["core","Core"],["rear_door","Rear Door"],["side_door","Side Door"],["hatch_door","Hatch Door"],
  ["insulation","Insulation Thickness"],["floor","Floor"],["reefer","Reefer Unit Model and Scope"],
  ["price_details","Price Details"],["payment_details","Payment Details"]
];

export default function PCBuilderPage(){
  const { qid, pcId, pcid } = useParams();
  const editPcId=pcId||pcid||null;
  const directPc=!qid&&!editPcId;
  const draftSource=editPcId?("edit-"+editPcId):(qid||"direct");
  const navigate = useNavigate();
  const {user}=useAuth();
  const [assistantOwners,setAssistantOwners]=useState([]);
  const duplicateId=new URLSearchParams(window.location.search).get("duplicate");

  useEffect(()=>{
    if(user?.role!=="assistant")return;
    api.get("/assistant/salespeople").then(r=>setAssistantOwners(r.data||[]))
      .catch(()=>toast.error("Could not load responsible salespeople"));
  },[user?.role]);
  // assistant-role-form-20260922

  const [form,setForm]=useState(null);
  const [masterContainers,setMasterContainers]=useState([]);
  const [masterVehicles,setMasterVehicles]=useState([]);
  const [masterAddons,setMasterAddons]=useState([]);

  useEffect(()=>{
    let active=true;
    api.get("/vehicles").then(r=>{if(active)setMasterVehicles(Array.isArray(r.data)?r.data:[]);})
      .catch(()=>{if(active)toast.error("Could not load vehicle master list");});
    api.get("/addons").then(r=>{if(active)setMasterAddons(Array.isArray(r.data)?r.data:[]);})
      .catch(()=>{if(active)toast.error("Could not load reefer master list");});
    return ()=>{active=false;};
  },[]);
  const [containerQuery,setContainerQuery]=useState("");
  const [selectedContainerId,setSelectedContainerId]=useState("");
  const [containerOpen,setContainerOpen]=useState(false);
  const containerPickerRef=useRef(null);
  useEffect(()=>{
    let active=true;
    api.get("/containers").then(r=>{
      if(active) setMasterContainers(Array.isArray(r.data)?r.data:[]);
    }).catch(()=>{if(active) toast.error("Could not load container master list");});
    return ()=>{active=false;};
  },[]);
  useEffect(()=>{
    if(!containerOpen) return;
    const close=(event)=>{
      if(containerPickerRef.current&&!containerPickerRef.current.contains(event.target)) setContainerOpen(false);
    };
    document.addEventListener("pointerdown",close,true);
    return ()=>document.removeEventListener("pointerdown",close,true);
  },[containerOpen]);
  const selectMasterContainer=(container)=>{
    if(!container) return;
    const identityText=[container.model,container.name,container.category,container.class_code,container.doc_code,container.box_size,container.size,container.external_dimensions].filter(Boolean).join(" ");
    const defaultNewExclusive=container.pc_box_exclusive_default===true||String(container.pc_type_default||"").toUpperCase()==="NEW"||/(?:^|[^0-9])(45|56)(?:[^0-9]|$)/.test(identityText);
    const modelName=String(container.model||"").trim().toUpperCase();
    const fishBox=/fish/i.test(String(container.rear_door||""))||/^\s*(GRC|ALP|ALC)\s*F/i.test(String(container.model||container.name||""));
    const standardGrpRoof=["GRC","ALP","ALC"].includes(modelName);
    const roofDefault=(fishBox||standardGrpRoof)?"GRP finish":(container.inside_roof||container.roof_finish||container.interior||"");
    // Selecting a master entry is an explicit choice to apply its approved specifications.
    // Only catalog-derived fields are changed; customer, pricing, PC numbering and vehicles stay intact.
    setForm(previous=>({
      ...previous,
      pc_type:defaultNewExclusive?"NEW":previous.pc_type,
      box_exclusive:defaultNewExclusive?true:!!previous.box_exclusive,
      container_type:container.model||container.name||"",
      external_size:container.external_dimensions||"",
      internal_size:container.internal_dimensions||container.internal_size||"",
      inside_wall:container.interior||"",
      inside_roof:roofDefault,
      outside_finish:container.exterior||"",
      base:container.base||"",
      core:container.core||"",
      price_details:container.standard_price!=null?String(Math.round((Number(container.standard_price||0)+pcAddonBasicPrice(pcReeferMaster(previous.reefer)))*100)/100):(previous.price_details||""),
      rear_door:container.rear_door||"",
      side_door:container.side_door||"",
      hatch_door:container.hatch_door||"",
      insulation:container.insulation||"",
      floor:container.flooring_remarks||container.floor||""
    }));
    const catalogValues={
      container_type:container.model||container.name||"",
      external_size:container.external_dimensions||"",
      internal_size:container.internal_dimensions||container.internal_size||"",
      inside_wall:container.interior||"",
      inside_roof:roofDefault,
      outside_finish:container.exterior||"",
      base:container.base||"",core:container.core||"",
      rear_door:container.rear_door||"",
      side_door:container.side_door||"",
      hatch_door:container.hatch_door||"",
      insulation:container.insulation||"",
      floor:container.flooring_remarks||container.floor||""
    };
    setForm(previous=>({...previous,master_defaults:catalogValues}));
    setSelectedContainerId(container.id);
    setContainerQuery("");
    setContainerOpen(false);
  };
  const activeContainers=masterContainers.filter(c=>c.is_active!==false&&c.active!==false&&!c.archived_at);
  const activeVehicles=masterVehicles.filter(v=>v.is_active!==false&&v.active!==false&&!v.archived_at);
  const activeAddons=masterAddons.filter(a=>a.is_active!==false&&a.active!==false&&!a.archived_at);
  const vehicleOptions=uniquePCOptions(activeVehicles.map(v=>v.name||v.model||v.vehicle_name));
  // The shared add-ons catalog contains accessories AND reefers: never add all accessories to the reefer picker.
  const reeferOptions=uniquePCOptions(activeAddons.filter(a=>{
    const group=String(a.type||a.addon_type||a.category||a.kind||"").toLowerCase();
    return group.includes("reefer")||group.includes("refer")||group.includes("refrigeration");
  }).map(a=>a.name||a.title||a.model||a.reefer_name||a.addon_name));
  // These PC properties correspond to the same container catalog values used in quotations.
  const containerSpecKeys={
    external_size:["external_dimensions"],
    internal_size:["internal_dimensions","internal_size"],
    inside_wall:["interior"],
    inside_roof:["inside_roof","roof_finish","interior"],
    outside_finish:["exterior"],
    base:["base"],
    rear_door:["rear_door"],
    side_door:["side_door"],
    hatch_door:["hatch_door"],
    insulation:["insulation"],
    floor:["flooring_remarks","floor"]
  };
  const pcAddonName=(a)=>String(a?.name||a?.model||a?.title||a?.addon_name||a?.reefer_name||"").trim();
  const pcAddonBasicPrice=(a)=>{
    const raw=a?.standard_price!=null && a?.standard_price!=="" ? a.standard_price : a?.price;
    const n=Number(raw);
    return Number.isFinite(n)?n:0;
  };
  const pcReeferMaster=(value)=>activeAddons.find(a=>{
    const group=String(a?.type||a?.addon_type||a?.category||a?.kind||"").toLowerCase();
    return (group.includes("reefer")||group.includes("refer")||group.includes("refrigeration")) &&
      pcAddonName(a).toLowerCase()===String(value||"").trim().toLowerCase();
  });
  const pcPriceTotal=(value)=>{
    const nums=String(value??"").replace(/,/g,"").match(/\d+(?:\.\d+)?/g)||[];
    return nums.reduce((sum,n)=>sum+Number(n||0),0);
  };
  const pcContainerMaster=(value)=>activeContainers.find(c=>
    [c?.model,c?.name].filter(Boolean).some(x=>String(x).trim().toLowerCase()===String(value||"").trim().toLowerCase()));
  const setReeferWithPrice=(value)=>{
    const next=pcReeferMaster(value);
    if(!next){set("reefer",value);return;}
    const previous=pcReeferMaster(form?.reefer);
    const previousPrice=pcAddonBasicPrice(previous);
    const container=pcContainerMaster(form?.container_type);
    const knownBase=container?.standard_price!=null && container.standard_price!==""?Number(container.standard_price):null;
    const fallbackBase=Math.max(0,pcPriceTotal(form?.price_details)-previousPrice);
    const base=Number.isFinite(knownBase)?knownBase:fallbackBase;
    const total=Math.round((base+pcAddonBasicPrice(next))*100)/100;
    setForm(current=>({...current,reefer:value,price_details:String(total)}));
  };
  // pc-master-price-and-live-tone-20260930
  const masterOptionsFor=k=>{
    if(k==="vehicle_model")return vehicleOptions;
    if(k==="reefer")return reeferOptions;
    const keys=containerSpecKeys[k]||[];
    return uniquePCOptions(activeContainers.flatMap(c=>keys.map(key=>c[key])));
  };
  const hasPCMaster=k=>k==="vehicle_model"||k==="reefer"||Object.prototype.hasOwnProperty.call(containerSpecKeys,k);
  // pc-container-production-defaults-20260930
  const visibleMasterContainers=masterContainers.filter(c=>{
    if(c.is_active===false||c.active===false||c.archived_at) return false;
    const terms=containerQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const text=[c.model,c.name,c.category,c.class_code,c.external_dimensions,c.internal_dimensions,
      c.interior,c.exterior,c.flooring_remarks].filter(Boolean).join(" ").toLowerCase();
    return terms.every(term=>text.includes(term));
  });

  const [saving,setSaving]=useState(false);
  useEffect(()=>{ let active=true; setForm(null); Promise.all([api.get(editPcId?`/pc-editor/${editPcId}`:directPc?"/direct-pc/draft":`/quotations/${qid}/pc-draft`),api.get(`/work-drafts/pc/${draftSource}`).catch(()=>({data:{payload:null}}))]).then(([base,saved])=>active&&setForm((()=>{const v=saved.data?.payload?{...base.data,...saved.data.payload}:base.data;return {...v,delivery_time:v.delivery_time||"20 DAYS"}})())).catch(e=>{if(!active)return;toast.error(e.response?.data?.detail||"Could not prepare PC"); navigate(editPcId?"/pcs":directPc?"/pcs":"/quotes");}); return()=>{active=false;}; },[qid,editPcId,draftSource,directPc,navigate]);
  useEffect(()=>{
    if(!duplicateId||!directPc)return;
    let live=true;
    Promise.all([api.get("/records/all/pcs"),api.get("/direct-pc/draft")]).then(([rows,base])=>{
      if(!live)return;
      const original=(rows.data||[]).find(p=>p.id===duplicateId);
      if(!original){toast.error("Original PC not found");return;}
      const fields=["customer_name","customer_address","contact","gst_number","container_type",
       "quantity","external_size","internal_size","inside_wall","inside_roof","outside_finish","base","core",
       "rear_door","side_door","hatch_door","insulation","floor","delivery_time","vehicle_model",
       "reefer","price_details","payment_details","special_customization","box_exclusive",
       "pc_uppercase","highlight_overrides","master_defaults","bill_together"];
      setForm(current=>{
        const copied={...current};
        fields.forEach(k=>{if(original[k]!==undefined)copied[k]=original[k];});
        copied.responsible_salesperson_id="";
        copied.expected_pc_preview=base.data?.next_pc_preview||"";
        copied.next_pc_preview=base.data?.next_pc_preview||"";
        return copied;
      });
    }).catch(()=>toast.error("Could not duplicate PC"));
    return()=>{live=false};
  },[duplicateId,directPc]);
  // assistant-pc-duplicate-20260922
  if(!form) return <div className="py-16 text-center text-slate-500">Preparing PC…</div>;
  const isStock=String(form?.price_details||"").trim().toUpperCase()==="STOCK";
  const selectStock=()=>setForm(v=>({...v,price_details:"STOCK",customer_name:"KERALA DEPOT STOCK",vehicle_model:"NA",payment_details:"NA",bill_together:true}));
  const set=(k,v)=>setForm(current=>({...current,[k]:v}));
  // PC_NUMBER_EXPECTED_AT_FINALIZE: never silently accept a different saved PC number.
  const shownPcNumber=()=>{
    const start=Number(String(form?.next_pc_preview||"").split("-")[0]);
    const qty=Number(form?.quantity||1);
    if(!Number.isInteger(start)||start<1)return String(form?.next_pc_preview||"");
    return qty>1&&form.bill_together?`${start}-${start+qty-1}`:String(start);
  };
  const cleanPayload=()=>{ const payload={...form}; for(const key of ["box_exclusive","pc_uppercase"])if(payload[key]===""||payload[key]==null)payload[key]=false; for(const key of ["highlight_overrides","master_defaults"])if(!payload[key]||typeof payload[key]!=="object"||Array.isArray(payload[key]))payload[key]={}; if(directPc)payload.expected_pc_preview=shownPcNumber(); delete payload.defaults; delete payload.next_pc_preview; delete payload.financial_year; delete payload.staff_prefix; return payload; };
  const preview=async(fmt)=>{
    if(!["NEW","ALLOT"].includes(form.pc_type)) return toast.error("Choose NEW or ALLOT");
    if(Number(form.quantity||0)<1) return toast.error("Quantity must be at least 1");
    try{
      const r=await api.post(editPcId?`/pc-editor/${editPcId}/preview/${fmt}`:directPc?`/direct-pc/preview/${fmt}`:`/quotations/${qid}/pc-preview/${fmt}`,cleanPayload(),{responseType:"blob"});
      const blob=new Blob([r.data],{type:fmt==="pdf"?"application/pdf":"application/vnd.openxmlformats-officedocument.wordprocessingml.document"});
      const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=`PC-PREVIEW.${fmt==="pdf"?"pdf":"docx"}`; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),1000);
      toast.success(`PC ${fmt==="pdf"?"PDF":"Word"} preview downloaded - no PC number was consumed`);
    }catch(e){
      let msg="PC preview failed";
      if(e.response?.data instanceof Blob){ try{ const t=await e.response.data.text(); const j=JSON.parse(t); msg=Array.isArray(j.detail)?j.detail.map(x=>[Array.isArray(x.loc)?x.loc.slice(1).join("."):"",x.msg||"Invalid value"].filter(Boolean).join(": ")).join("; "):(typeof j.detail==="string"?j.detail:msg); }catch(_){} }
      else msg=e.response?.data?.detail||msg;
      toast.error(msg);
    }
  };
  const savePcDraft=async()=>{if(!form)return;setSaving(true);try{const payload={...form};delete payload.defaults;delete payload.next_pc_preview;delete payload.financial_year;delete payload.staff_prefix;await api.post(`/work-drafts/pc/${draftSource}`,{payload});toast.success("PC draft saved")}catch(e){toast.error(e.response?.data?.detail||"Could not save PC draft")}finally{setSaving(false)}};
  const pcInputTone=(k)=>{
    const override=form?.highlight_overrides?.[k];
    if(override===true)return "text-red-600";
    if(override===false)return "text-slate-900";
    const master=form?.master_defaults?.[k];
    if(master!==undefined&&String(form?.[k]??"").trim().toLowerCase()!==String(master??"").trim().toLowerCase())return "text-red-600";
    if(k==="special_customization"&&String(form?.[k]||"").trim())return "text-red-600";
    return "text-slate-900";
  };
  // pc-workflow-refinements-20260921
  const submit=async()=>{
    if(!["NEW","ALLOT"].includes(form.pc_type)) return toast.error("Choose NEW or ALLOT");
    if(Number(form.quantity||0)<1) return toast.error("Quantity must be at least 1");
    if(user?.role==="assistant"&&!form.responsible_salesperson_id)return toast.error("Select the responsible salesperson");
    setSaving(true);
    try{
      const {data}=editPcId?await api.put(`/pc-editor/${editPcId}`,cleanPayload()):await api.post(directPc?"/direct-pc/create":`/quotations/${qid}/confirm`,cleanPayload());
      await api.delete(`/work-drafts/pc/${draftSource}`).catch(()=>{}); toast.success("PC "+data.pc_number+(editPcId?" updated":" created"));
      if(data.owner_change_pending)toast.message("Responsible salesperson change is pending approval");
      navigate("/pcs");
    }catch(e){ toast.error(e.response?.data?.detail||"PC creation failed"); }
    finally{ setSaving(false); }
  };
  return <div className="max-w-5xl mx-auto pb-20">
    <div className="mb-5"><h1 className="text-2xl font-semibold text-slate-900">{editPcId?"Edit Production Order Confirmation":"Create Production Order Confirmation"}</h1><p className="text-sm text-slate-500 mt-1">{editPcId?"Edit this existing PC using the same controls as New PC creation. The allocated PC number is locked.":directPc?"Create a PC without a quotation. Review every production-critical field before finalizing.":"Review every production-critical field before finalizing. Blank rows will remain blank in the PC."}</p></div>
    {user?.role==="assistant"&&<div className="fc-card p-4 space-y-2 border-blue-200">
      <Label>Responsible Salesperson *</Label>
      <select aria-label="Responsible Salesperson" value={form.responsible_salesperson_id||""}
       onChange={e=>setForm(f=>({...f,responsible_salesperson_id:e.target.value}))}
       className="w-full h-11 rounded-xl border border-blue-300 bg-white px-3">
       <option value="">Choose the salesperson responsible for this PC</option>
       {assistantOwners.map(o=><option key={o.id} value={o.id}>{o.name} {o.pc_prefix?"("+o.pc_prefix+")":""}</option>)}
      </select>
      <p className="text-xs text-slate-600">The selected owner's prefix is used when the PC is finalized. Created by remains your Assistant account.</p>
    </div>}
    <div className="fc-card p-5 space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>PC Type *</Label><select value={form.pc_type} onChange={e=>set("pc_type",e.target.value)} className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 bg-white px-3"><option value="ALLOT">ALLOT</option><option value="NEW">NEW</option></select></div>
        <div><Label>PC Date *</Label><Input aria-label="PC Date" data-testid="pc-date" value={form.pc_date||""} onChange={e=>set("pc_date",e.target.value)} placeholder="DD/MM/YYYY" className="mt-1.5" /></div>
      </div>
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm"><div><b>{editPcId?"PC Number (locked):":"Next PC:"}</b> {directPc?shownPcNumber():form.next_pc_preview}</div><div><b>Financial Year:</b> {form.financial_year}</div><div><b>Staff prefix:</b> {user?.role==="assistant"?(assistantOwners.find(o=>o.id===form.responsible_salesperson_id)?.pc_prefix||"Select salesperson"):(form.staff_prefix || "Disabled / not configured")}</div></div>
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-3">
        <div className="text-sm font-semibold text-blue-900">PC document options</div>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Add BOX EXCLUSIVE before the container name in this PC only</span>
          <Switch data-testid="pc-box-exclusive-toggle" checked={!!form.box_exclusive}
            onCheckedChange={v=>set("box_exclusive",v)}/>
        </label>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Print production specifications in UPPERCASE for this PC</span>
          <Switch data-testid="pc-uppercase-toggle" checked={!!form.pc_uppercase}
            onCheckedChange={v=>set("pc_uppercase",v)}/>
        </label>
        <p className="text-xs text-slate-600">Default: original catalog text. PDF and Word previews use these settings; catalog names are unchanged.</p>
      </div>
      <div><Label>Order Quantity *</Label><Input type="number" min="1" value={form.quantity} onChange={e=>set("quantity",Number(e.target.value))} className="mt-1.5" /></div>
      {Number(form.quantity)>1 && !isStock && <label className="flex items-center justify-between gap-4 p-3 rounded-xl border border-slate-200"><div><div className="text-sm font-medium">Bill all units together</div><div className="text-xs text-slate-500 mt-0.5">Only keep this enabled when customer and billing details are exactly the same for every unit.</div></div><Switch checked={!!form.bill_together} onCheckedChange={v=>set("bill_together",v)} /></label>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.filter(([k])=>!(isStock&&k==="vehicle_model")).map(([k,label])=><div key={k} className={(k==="customer_address"||k==="reefer"||k==="price_details"?"sm:col-span-2":"")+" grid grid-cols-[72px_minmax(0,1fr)] sm:grid-cols-[76px_minmax(0,1fr)] gap-x-2 items-start min-w-0"}><Label className="col-span-2">{label}</Label>
          <select aria-label={"Highlight "+label} data-testid={"pc-highlight-"+k}
            value={form.highlight_overrides?.[k]===true?"red":form.highlight_overrides?.[k]===false?"black":"auto"}
            onChange={e=>set("highlight_overrides",(()=>{const next={...(form.highlight_overrides||{})};if(e.target.value==="auto")delete next[k];else next[k]=e.target.value==="red";return next;})())}
            className="mt-1.5 h-11 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-1 text-xs text-slate-600">
            <option value="auto">Auto</option>
            <option value="red">Red</option>
            <option value="black">Black</option>
          </select>{k==="customer_name"&&isStock?<Input value="KERALA DEPOT STOCK" readOnly className="mt-1.5 bg-slate-100"/>:
        k==="price_details"?<div className="mt-1.5 space-y-2">
          <Textarea value={form.price_details||""} onChange={e=>{const v=e.target.value;if(v.trim().toUpperCase()==="STOCK")selectStock();else setForm(prev=>{const wasStock=String(prev.price_details||"").trim().toUpperCase()==="STOCK";return {...prev,price_details:v,...(wasStock?{customer_name:"",vehicle_model:"",payment_details:""}:{})};});}} className="min-h-20" placeholder="Enter rate or type STOCK"/>
          {(directPc||isStock)&&/[a-z]/i.test(String(form.price_details||""))&&<button type="button"
             onClick={selectStock} className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800">
             Select STOCK — Kerala Depot Stock
           </button>}
          {isStock&&<div className="text-xs text-slate-600">Customer is automatically Kerala Depot Stock. No chassis or selling price required. Physical readiness is not verified.</div>}
        </div>:
        k==="container_type"?<div ref={containerPickerRef} className="relative mt-1.5" data-testid="pc-integrated-container-picker">
          <div className="relative">
            <Input value={form[k]||""}
              onFocus={e=>{setContainerQuery("");setContainerOpen(true);requestAnimationFrame(()=>e.target.select?.());}}
              onChange={e=>{set(k,e.target.value);setSelectedContainerId("");setContainerQuery(e.target.value);setContainerOpen(true);}}
              placeholder="Enter or choose container type"
              className={"h-11 w-full min-w-0 bg-white pr-7 text-sm "+pcInputTone(k)}
              role="combobox" aria-expanded={containerOpen} aria-autocomplete="list"
              data-testid="pc-container-type" />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">⌄</span>
          </div>
          {containerOpen&&<div className="absolute z-50 top-full mt-1 max-h-60 w-full overflow-auto rounded-xl border border-blue-200 bg-white shadow-xl">
            {visibleMasterContainers.length?visibleMasterContainers.map(c=>
              <button key={c.id} type="button" data-testid="pc-container-master-option"
                onClick={()=>selectMasterContainer(c)}
                className="block w-full whitespace-normal break-words border-b border-slate-100 px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50">
                <span className="block text-sm font-semibold text-slate-800">{c.model||c.name}</span>
                <span className="block text-xs text-slate-500">{[c.name!==c.model?c.name:"",c.class_code,c.external_dimensions].filter(Boolean).join(" · ")}</span>
              </button>
            ):<div className="px-3 py-3 text-xs text-slate-500">{masterContainers.length?"No matching master container. Keep typing to use a custom value.":"Container master list is empty or unavailable"}</div>}
          </div>}
        </div>:k==="contact"?<Input value={form[k]||""} maxLength={80}
          onChange={e=>{let digits=0;const v=[...e.target.value].filter(ch=>!/[0-9]/.test(ch)||++digits<=10).join("");set(k,v)}} className={"mt-1.5 "+pcInputTone(k)} placeholder="Contact person - 10 digit mobile"/>:
        k==="gst_number"?<Input value={form[k]||""} maxLength={15}
          onChange={e=>set(k,e.target.value.toUpperCase().slice(0,15))} className={"mt-1.5 "+pcInputTone(k)} placeholder="Maximum 15 characters"/>:
        hasPCMaster(k)?<PCMasterPicker label={label} value={form[k]||""}
          onChange={v=>k==="reefer"?setReeferWithPrice(v):set(k,v)} options={masterOptionsFor(k)}
          allowMultiline={k==="reefer"} tone={pcInputTone(k)}/>:
        k==="customer_address"||k==="price_details"?<Textarea aria-label={label} data-testid={"pc-field-"+k} value={form[k]||""} onChange={e=>set(k,e.target.value)} className={"mt-1.5 min-h-20 "+pcInputTone(k)}/>:<Input aria-label={label} data-testid={"pc-field-"+k} value={form[k]||""} onChange={e=>set(k,e.target.value)} className={"mt-1.5 "+pcInputTone(k)}/>}</div>)}
        <div><Label>Delivery Time</Label><select value={form.delivery_time||""} onChange={e=>set("delivery_time",e.target.value)} className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 bg-white px-3"><option value="10 DAYS">10 DAYS</option><option value="15 DAYS">15 DAYS</option><option value="20 DAYS">20 DAYS</option><option value="25 DAYS">25 DAYS</option><option value="30 DAYS">30 DAYS</option><option value={form.delivery_time||"CUSTOM"}>{form.delivery_time && !["10 DAYS","15 DAYS","20 DAYS"].includes(form.delivery_time)?form.delivery_time:"Custom / current"}</option></select></div>
      </div>
      <div><Label>Special Customization</Label>
        <select aria-label="Highlight Special Customization"
          value={form.highlight_overrides?.special_customization===true?"red":form.highlight_overrides?.special_customization===false?"black":"auto"}
          onChange={e=>set("highlight_overrides",(()=>{const next={...(form.highlight_overrides||{})};if(e.target.value==="auto")delete next.special_customization;else next.special_customization=e.target.value==="red";return next;})())}
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
          <option value="auto">Auto</option><option value="red">Red</option><option value="black">Black</option>
        </select><Textarea value={form.special_customization||""} onChange={e=>set("special_customization",e.target.value)} className={"mt-1.5 min-h-24 "+pcInputTone("special_customization")} placeholder="Leave blank when not applicable"/><p className="text-[11px] text-slate-500 mt-1">For NEW PCs, non-standard changes and special customization are highlighted in red in the final PC.</p></div>
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-3"><div className="text-sm font-medium text-blue-900">Preview before finalizing</div><div className="text-xs text-blue-700 mt-1">Preview uses the next PC number only for display. It does not reserve or consume that number.</div><div className="grid grid-cols-2 gap-2 mt-3"><button type="button" onClick={()=>preview("word")} className="h-11 rounded-xl border border-blue-300 bg-white text-blue-700 text-sm font-semibold">Preview Word</button><button type="button" onClick={()=>preview("pdf")} className="h-11 rounded-xl border border-blue-300 bg-white text-blue-700 text-sm font-semibold">Preview PDF</button></div></div>
      <div className="grid sm:grid-cols-2 gap-2"><button disabled={saving} onClick={savePcDraft} className="w-full h-12 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-semibold disabled:opacity-60">Save Draft</button><button disabled={saving} onClick={submit} className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-60">{saving?"Working…":editPcId?"Save PC Changes":"Finalize PC"}</button></div><p className="text-[11px] text-slate-500">Drafts do not consume a PC number. {editPcId?"Editing never allocates a new PC number. A saved draft resumes this same existing PC.":"Reopening this PC creation form resumes your saved draft. No PC number is used until Finalize."}</p>
    </div>
  </div>;
}

// pc-wide-readable-values-20260930

// pc-edit-builder-parity-20261002

// pc-conversion-editable-20261012

// fc-stock-ui-20261012

// fc-stock-conditional-button-spec-match-20261012
